import { useState, useEffect } from "react"
import {
  getPlatformInfo,
  getPlatformSupport,
  isFeatureSupported,
  type Platform,
  type PlatformInfo,
  type PlatformSupport,
} from "@/model/NativeBridge/platform"
import { addLogError } from "@/model/logger"

/**
 * Hook for accessing platform information and capabilities
 *
 * This hook provides comprehensive platform detection including:
 * - Platform type (iOS, Android, Web)
 * - Platform-specific capabilities (Bluetooth, HealthKit, etc.)
 * - Convenience flags for common platform checks
 *
 * @returns Object containing platform information, support flags, and loading state
 *
 * @example
 * function MyComponent() {
 *   const { isIOS, hasBluetooth, loading } = usePlatform()
 *
 *   if (loading) return <Spinner />
 *
 *   if (isIOS && hasBluetooth) {
 *     // Show iOS Bluetooth features
 *   }
 * }
 */
export function usePlatform() {
  const [platform, setPlatform] = useState<Platform | null>(null)
  const [platformInfo, setPlatformInfo] = useState<PlatformInfo | null>(null)
  const [platformSupport, setPlatformSupport] = useState<PlatformSupport | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadPlatformData() {
      try {
        const [info, support] = await Promise.all([getPlatformInfo(), getPlatformSupport()])

        setPlatformInfo(info)
        setPlatformSupport(support)
        setPlatform(info?.platform || null)
      } catch (error) {
        addLogError("Failed to load platform data:", error)
      } finally {
        setLoading(false)
      }
    }

    void loadPlatformData()
  }, [])

  return {
    platform,
    platformInfo,
    platformSupport,
    loading,
    isIOS: platform === "ios",
    isAndroid: platform === "android",
    isWeb: platform === null && !loading,
    // Platform-specific features
    hasAppleWatch: platformSupport?.appleWatch === true,
    hasAppleHealth: platformSupport?.appleHealth === true,
    hasWorkoutKit: platformSupport?.workoutKit === true,
    hasHealthKit: platformSupport?.healthKit === true,
    hasGoogleFit: platformSupport?.googleFit === true,
    hasBluetooth: platformSupport?.bluetooth === true,
  }
}

/**
 * Hook for checking a specific platform feature
 *
 * Useful when you only need to check one specific feature
 * without loading all platform information
 *
 * @param feature - The feature to check (e.g., 'bluetooth', 'workoutKit')
 * @returns true if the feature is supported, false otherwise
 *
 * @example
 * function BluetoothButton() {
 *   const hasBluetoothSupport = usePlatformFeature('bluetooth')
 *
 *   if (!hasBluetoothSupport) {
 *     return <DisabledButton>Bluetooth not supported</DisabledButton>
 *   }
 *
 *   return <BluetoothConnectButton />
 * }
 */
export function usePlatformFeature(feature: keyof PlatformSupport): boolean {
  const [supported, setSupported] = useState(false)

  useEffect(() => {
    isFeatureSupported(feature)
      .then(setSupported)
      .catch((error) => addLogError("Failed to check platform feature support", error))
  }, [feature])

  return supported
}
